/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { resolveImageUrl } from '@/components/ImageUpload';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Link2, Plus, Copy, Trash2, Edit2, ExternalLink, Loader2, ImagePlus,
  Store as StoreIcon, Repeat, CheckCircle2, Car, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { PAYLINK_LANGS } from '@/lib/paylinkI18n';
import { formatMoney as money } from '@/lib/adminFormat';

const CURRENCIES = [
  { code: 'eur', label: 'EUR · Euro (€)' },
  { code: 'usd', label: 'USD · Dollar US ($)' },
  { code: 'gbp', label: 'GBP · Livre sterling (£)' },
  { code: 'pln', label: 'PLN · Złoty polonais (zł)' },
  { code: 'sek', label: 'SEK · Couronne suédoise (kr)' },
];

const emptyForm = {
  name: '', stripe_name: '', price: '', currency: 'eur', image_url: '', description: '',
  checkout_mode: 'element', attach_mode: 'store', store_id: '',
  vitrine_pool: [], rotation_mode: 'manual', active_vitrine_id: '', active: true, template: 'classic',
  language: 'auto', meta_pixel_id: '', tiktok_pixel_id: '',
};

function LinkForm({ open, onClose, editing, stores, vitrines, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        ...emptyForm, ...editing,
        price: String(editing.price ?? ''),
        vitrine_pool: editing.vitrine_pool || [],
      } : emptyForm);
    }
  }, [open, editing]);

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      upd('image_url', data.url);
      toast.success('Photo ajoutée');
    } catch (e) {
      toast.error('Échec de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const togglePoolVitrine = (vid) => {
    setForm((p) => {
      const has = p.vitrine_pool.some((e) => e.vitrine_id === vid);
      const pool = has
        ? p.vitrine_pool.filter((e) => e.vitrine_id !== vid)
        : [...p.vitrine_pool, { vitrine_id: vid, daily_cap_eur: 0 }];
      let active = p.active_vitrine_id;
      if (has && active === vid) active = pool[0]?.vitrine_id || '';
      if (!has && !active) active = vid;
      return { ...p, vitrine_pool: pool, active_vitrine_id: active };
    });
  };

  const setCap = (vid, val) => setForm((p) => ({
    ...p,
    vitrine_pool: p.vitrine_pool.map((e) => e.vitrine_id === vid ? { ...e, daily_cap_eur: Number(val) || 0 } : e),
  }));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Nom du produit requis'); return; }
    if (!(Number(form.price) > 0)) { toast.error('Prix invalide'); return; }
    if (form.attach_mode === 'store' && !form.store_id) { toast.error('Choisissez une boutique'); return; }
    if (form.attach_mode === 'independent' && form.vitrine_pool.length === 0) { toast.error('Ajoutez au moins un compte Stripe au pool'); return; }
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price) };
      if (editing) await api.put(`/payment-links/${editing.id}`, payload);
      else await api.post('/payment-links', payload);
      toast.success(editing ? 'Lien mis à jour' : 'Lien créé');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="pl-form-dialog">
        <DialogHeader><DialogTitle>{editing ? 'Modifier le lien' : 'Nouveau lien de paiement'}</DialogTitle><DialogDescription className="sr-only">Produit, prix et paramètres du lien.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <Label className="text-xs">NOM DU PRODUIT (vu par le client)</Label>
            <Input data-testid="pl-name-input" value={form.name} onChange={(e) => upd('name', e.target.value)} placeholder="Ex. Sac cuir premium" className="mt-1.5 h-10" />
          </div>
          <div>
            <Label className="text-xs">NOM CÔTÉ STRIPE (métadonnées) — optionnel</Label>
            <Input data-testid="pl-stripe-name-input" value={form.stripe_name} onChange={(e) => upd('stripe_name', e.target.value)} placeholder="Vide = même nom que ci-dessus" className="mt-1.5 h-10" />
            <p className="text-xs text-zinc-500 mt-1">Nom envoyé à Stripe (description + métadonnées de la transaction). N'est pas affiché au client.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">PRIX</Label>
              <Input data-testid="pl-price-input" type="number" step="0.01" value={form.price} onChange={(e) => upd('price', e.target.value)} placeholder="149.90" className="mt-1.5 h-10" />
            </div>
            <div>
              <Label className="text-xs">DEVISE</Label>
              <Select value={form.currency} onValueChange={(v) => upd('currency', v)}>
                <SelectTrigger data-testid="pl-currency-select" className="mt-1.5 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code} data-testid={`pl-currency-${c.code}`}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">PHOTO</Label>
            <div className="mt-1.5 flex items-center gap-3">
              {form.image_url ? (
                <img src={resolveImageUrl(form.image_url)} alt="" className="h-16 w-16 rounded-md object-cover border border-zinc-200" />
              ) : (
                <div className="h-16 w-16 rounded-md border border-dashed border-zinc-300 flex items-center justify-center text-zinc-400"><ImagePlus className="h-5 w-5" /></div>
              )}
              <label className="cursor-pointer">
                <input data-testid="pl-image-input" type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
                <span className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-zinc-200 text-sm hover:bg-zinc-50">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Choisir une image
                </span>
              </label>
              {form.image_url && <Button data-testid="pl-image-remove" variant="ghost" size="sm" onClick={() => upd('image_url', '')} className="text-zinc-500">Retirer</Button>}
            </div>
          </div>
          <div>
            <Label className="text-xs">DESCRIPTION (option.)</Label>
            <Textarea data-testid="pl-description-input" value={form.description} onChange={(e) => upd('description', e.target.value)} rows={2} className="mt-1.5" />
          </div>

          <div>
            <Label className="text-xs">EXPÉRIENCE DE PAIEMENT</Label>
            <div className="mt-1.5 grid grid-cols-2 gap-1 p-1 bg-zinc-100 rounded-lg">
              {[['element', 'Checkout intégré'], ['hosted', 'Page Stripe']].map(([k, lbl]) => (
                <button key={k} type="button" data-testid={`pl-mode-${k}`} onClick={() => upd('checkout_mode', k)}
                  className={`h-9 rounded-md text-sm font-medium transition ${form.checkout_mode === k ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-500'}`}>{lbl}</button>
              ))}
            </div>
          </div>

          {form.checkout_mode === 'element' && (
            <div>
              <Label className="text-xs">STYLE DE PAGE</Label>
              <div className="mt-1.5 grid grid-cols-3 gap-1 p-1 bg-zinc-100 rounded-lg">
                {[['classic', 'Épuré'], ['shopify', 'Style Shopify'], ['lumen', 'Lumen']].map(([k, lbl]) => (
                  <button key={k} type="button" data-testid={`pl-template-${k}`} onClick={() => upd('template', k)}
                    className={`h-9 rounded-md text-sm font-medium transition ${form.template === k ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-500'}`}>{lbl}</button>
                ))}
              </div>
              <p className="text-xs text-zinc-500 mt-1">« Shopify » : page 2 colonnes optimisée conversion. « Lumen » : premium, centré et épuré.</p>
            </div>
          )}

          <div>
            <Label className="text-xs">LANGUE DU CHECKOUT</Label>
            <Select value={form.language || 'auto'} onValueChange={(v) => upd('language', v)}>
              <SelectTrigger data-testid="pl-language-select" className="mt-1.5 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYLINK_LANGS.map((l) => <SelectItem key={l.code} value={l.code} data-testid={`pl-language-${l.code}`}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-zinc-500 mt-1">« Auto » : la page s’affiche dans la langue du navigateur du client (repli sur le français).</p>
          </div>

          <div>
            <Label className="text-xs">ENCAISSEMENT</Label>
            <div className="mt-1.5 grid grid-cols-2 gap-1 p-1 bg-zinc-100 rounded-lg">
              {[['store', 'Rattaché à une boutique'], ['independent', 'Indépendant (rotation)']].map(([k, lbl]) => (
                <button key={k} type="button" data-testid={`pl-attach-${k}`} onClick={() => upd('attach_mode', k)}
                  className={`h-9 rounded-md text-xs font-medium transition ${form.attach_mode === k ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-500'}`}>{lbl}</button>
              ))}
            </div>
          </div>

          {form.attach_mode === 'store' ? (
            <div>
              <Label className="text-xs">BOUTIQUE</Label>
              <Select value={form.store_id} onValueChange={(v) => upd('store_id', v)}>
                <SelectTrigger data-testid="pl-store-select" className="mt-1.5 h-10"><SelectValue placeholder="Choisir une boutique" /></SelectTrigger>
                <SelectContent>
                  {stores.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`pl-store-option-${s.id}`}>{s.name} · {(s.currency || 'eur').toUpperCase()}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-1">Utilise le Stripe, le branding et la rotation Batmobile de la boutique.</p>
            </div>
          ) : (
            <div className="space-y-3 rounded-lg border border-zinc-200 p-3">
              <p className="text-xs text-zinc-600 font-medium flex items-center gap-1.5"><Car className="h-3.5 w-3.5" /> Pool de rotation (comptes Stripe)</p>
              <div className="space-y-2 max-h-44 overflow-y-auto">
                {vitrines.length === 0 && <p className="text-xs text-zinc-400">Aucune vitrine disponible. Créez-en dans « Sites Vitrines ».</p>}
                {vitrines.map((v) => {
                  const inPool = form.vitrine_pool.some((e) => e.vitrine_id === v.id);
                  const entry = form.vitrine_pool.find((e) => e.vitrine_id === v.id);
                  return (
                    <div key={v.id} className={`rounded-md border p-2 ${inPool ? 'border-zinc-900' : 'border-zinc-200'}`}>
                      <div className="flex items-center justify-between gap-2">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input type="checkbox" data-testid={`pl-vitrine-${v.id}`} checked={inPool} onChange={() => togglePoolVitrine(v.id)} />
                          {v.name}
                        </label>
                        {inPool && (
                          <button type="button" data-testid={`pl-set-active-${v.id}`} onClick={() => upd('active_vitrine_id', v.id)}
                            className={`text-xs px-2 py-0.5 rounded-full ${form.active_vitrine_id === v.id ? 'bg-emerald-100 text-emerald-700' : 'text-zinc-400 hover:text-zinc-600'}`}>
                            {form.active_vitrine_id === v.id ? 'Actif' : 'Activer'}
                          </button>
                        )}
                      </div>
                      {inPool && (
                        <div className="mt-2 flex items-center gap-2">
                          <Label className="text-[11px] text-zinc-400">Plafond/jour</Label>
                          <Input type="number" value={entry?.daily_cap_eur || 0} onChange={(e) => setCap(v.id, e.target.value)} className="h-8 w-28 text-xs" placeholder="0 = illimité" />
                        </div>
                      )}
                    </div>
                  );
                })}
                {form.vitrine_pool
                  .filter((e) => !vitrines.some((v) => v.id === e.vitrine_id))
                  .map((e) => (
                    <div key={e.vitrine_id} data-testid={`pl-dead-vitrine-${e.vitrine_id}`}
                      className="rounded-md border border-red-300 bg-red-50 p-2 flex items-center justify-between gap-2">
                      <span className="text-xs text-red-600 flex items-center gap-1.5 min-w-0">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">Compte Stripe supprimé — à retirer</span>
                      </span>
                      <button type="button" data-testid={`pl-remove-dead-${e.vitrine_id}`}
                        onClick={() => togglePoolVitrine(e.vitrine_id)}
                        className="text-xs px-2 py-0.5 rounded-full bg-red-600 text-white hover:bg-red-700 shrink-0">
                        Retirer
                      </button>
                    </div>
                  ))}
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Mode</Label>
                <div className="grid grid-cols-2 gap-1 p-1 bg-zinc-100 rounded-lg flex-1">
                  {[['manual', 'Manuel'], ['auto', 'Auto (plafonds)']].map(([k, lbl]) => (
                    <button key={k} type="button" data-testid={`pl-rot-${k}`} onClick={() => upd('rotation_mode', k)}
                      className={`h-8 rounded-md text-xs font-medium transition ${form.rotation_mode === k ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-500'}`}>{lbl}</button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-zinc-500">En manuel, le compte « Actif » encaisse. En auto, la rotation bascule au plafond journalier atteint.</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">PIXEL META (option.)</Label>
              <Input data-testid="pl-meta-pixel-input" value={form.meta_pixel_id} onChange={(e) => upd('meta_pixel_id', e.target.value)} placeholder="Ex. 1234567890" className="mt-1.5 h-10" />
            </div>
            <div>
              <Label className="text-xs">PIXEL TIKTOK (option.)</Label>
              <Input data-testid="pl-tiktok-pixel-input" value={form.tiktok_pixel_id} onChange={(e) => upd('tiktok_pixel_id', e.target.value)} placeholder="Ex. C1AB2..." className="mt-1.5 h-10" />
            </div>
          </div>

          <div className="flex items-center justify-between pt-1">
            <Label className="text-sm">Lien actif</Label>
            <Switch data-testid="pl-active-switch" checked={form.active} onCheckedChange={(v) => upd('active', v)} />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="pl-cancel-button" variant="ghost" onClick={onClose}>Annuler</Button>
          <Button data-testid="pl-save-button" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}{editing ? 'Enregistrer' : 'Créer le lien'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PaymentLinks() {
  const [links, setLinks] = useState([]);
  const [stores, setStores] = useState([]);
  const [vitrines, setVitrines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s, v] = await Promise.all([
        api.get('/payment-links'),
        api.get('/stores'),
        api.get('/vitrines'),
      ]);
      setLinks(l.data || []);
      setStores(s.data || []);
      setVitrines(v.data || []);
    } catch (e) {
      toast.error('Chargement impossible');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const vmap = useMemo(() => Object.fromEntries(vitrines.map((v) => [v.id, v.name])), [vitrines]);
  const smap = useMemo(() => Object.fromEntries(stores.map((s) => [s.id, s.name])), [stores]);

  const copyLink = (id) => {
    const url = `${window.location.origin}/pay/${id}`;
    navigator.clipboard.writeText(url);
    toast.success('Lien copié !', { description: url });
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce lien de paiement ?')) return;
    try { await api.delete(`/payment-links/${id}`); toast.success('Supprimé'); load(); }
    catch { toast.error('Suppression impossible'); }
  };

  const switchVitrine = async (linkId, vid) => {
    try { await api.post(`/payment-links/${linkId}/activate-vitrine`, { vitrine_id: vid }); toast.success('Compte Stripe basculé'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Bascule impossible'); }
  };

  return (
    <div className="space-y-6" data-testid="payment-links-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="dashboard-eyebrow">Encaissement</div>
          <h1 data-testid="payment-links-title">Liens de paiement</h1>
          <p className="dashboard-caption" data-testid="payment-links-count">{loading ? 'Chargement…' : `${links.length} lien${links.length > 1 ? 's' : ''} · ${links.filter((l) => l.active).length} actif${links.filter((l) => l.active).length > 1 ? 's' : ''}`}</p>
        </div>
        <Button data-testid="pl-new-button" onClick={() => { setEditing(null); setOpen(true); }}><Plus className="h-4 w-4 mr-2" /> Nouveau lien</Button>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
      ) : links.length === 0 ? (
        <Card className="p-10 text-center text-zinc-500" data-testid="pl-empty">
          <Link2 className="h-8 w-8 mx-auto text-zinc-300 mb-3" />
          Aucun lien pour l'instant. Créez votre premier lien de paiement.
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {links.map((l) => (
            <Card key={l.id} className="card-flat overflow-hidden" data-testid={`pl-card-${l.id}`}>
              {l.image_url ? <div className="aspect-[16/9] bg-zinc-100"><img src={resolveImageUrl(l.image_url)} alt={l.name} className="w-full h-full object-contain p-3" onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }} /></div> : null}
              <div className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p data-testid={`pl-name-${l.id}`} className="font-semibold text-zinc-900 truncate">{l.name}</p>
                    <p data-testid={`pl-price-${l.id}`} className="text-lg font-medium text-zinc-900 mt-1 tabular-nums">{money(l.price, l.currency)}</p>
                  </div>
                  <span data-testid={`pl-status-${l.id}`} className={`admin-status ${l.active ? 'admin-status-paid' : 'admin-status-neutral'}`}>{l.active ? 'Actif' : 'Inactif'}</span>
                </div>

                <div className="flex flex-wrap gap-1.5 text-[11px]">
                  <span className="px-2 py-0.5 rounded-md bg-zinc-100 text-zinc-600">{l.checkout_mode === 'hosted' ? 'Page Stripe' : 'Checkout intégré'}</span>
                  {l.attach_mode === 'store'
                    ? <span className="px-2 py-0.5 rounded-md bg-zinc-100 text-zinc-600 inline-flex items-center gap-1"><StoreIcon className="h-3 w-3" /> {smap[l.store_id] || 'Boutique'}</span>
                    : <span className="px-2 py-0.5 rounded-md bg-zinc-100 text-zinc-600 inline-flex items-center gap-1"><Repeat className="h-3 w-3" /> {l.rotation_mode === 'auto' ? 'Rotation auto' : 'Rotation manuelle'}</span>}
                  {l.paid_count > 0 && <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> {l.paid_count} payé{l.paid_count > 1 ? 's' : ''}</span>}
                </div>

                {l.attach_mode === 'independent' && (l.vitrine_pool || []).length > 0 && (
                  <div className="rounded-md bg-zinc-50 border border-zinc-100 p-2 space-y-1">
                    <p className="text-[11px] text-zinc-400 uppercase tracking-wide">Compte Stripe encaisseur</p>
                    <div className="flex flex-wrap gap-1">
                      {(l.vitrine_pool || []).map((e) => {
                        const dead = !vmap[e.vitrine_id];
                        return (
                          <button key={e.vitrine_id} data-testid={`pl-switch-${l.id}-${e.vitrine_id}`} onClick={() => switchVitrine(l.id, e.vitrine_id)}
                            className={`text-[11px] px-2 py-0.5 rounded-full border transition inline-flex items-center gap-1 ${dead ? 'bg-red-50 text-red-600 border-red-300' : l.active_vitrine_id === e.vitrine_id ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-200 text-zinc-500 hover:border-zinc-400'}`}>
                            {dead && <AlertTriangle className="h-3 w-3" />}
                            {dead ? 'Stripe supprimé' : vmap[e.vitrine_id]}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-1.5 pt-1">
                  <Button size="sm" variant="outline" data-testid={`pl-copy-${l.id}`} onClick={() => copyLink(l.id)} className="flex-1"><Copy className="h-3.5 w-3.5 mr-1.5" /> Copier</Button>
                  <Button size="sm" variant="outline" data-testid={`pl-preview-${l.id}`} onClick={() => window.open(`/pay/${l.id}`, '_blank', 'noopener,noreferrer')} title="Aperçu" aria-label={`Aperçu de ${l.name}`}><ExternalLink className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" data-testid={`pl-edit-${l.id}`} onClick={() => { setEditing(l); setOpen(true); }} title="Modifier" aria-label={`Modifier ${l.name}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" data-testid={`pl-delete-${l.id}`} onClick={() => remove(l.id)} className="text-red-600 hover:text-red-700" title="Supprimer" aria-label={`Supprimer ${l.name}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <LinkForm open={open} onClose={() => setOpen(false)} editing={editing} stores={stores} vitrines={vitrines} onSaved={load} />
    </div>
  );
}
